import DialogTemplate from '@/components/common/dialog-template/dialog-template';
import ListStylesContainer from '@/components/common/list-container/list-styles-container';
import { composeUri } from '@/utils/compose';
import { BIG_AGENCY_MANAGER, BIG_AGENCY_OPERATOR, GROUP_USERS, USERS } from '@/utils/CONST';
import { GroupUser, User } from '@/utils/types';
import { CancelOutlined, PersonOutlined } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import LoadingButton from '@mui/lab/LoadingButton';
import { useQueryClient } from '@tanstack/react-query';
import { map } from 'lodash';
import { RefreshButton, TopToolbar } from 'ra-ui-materialui';
import { useState } from 'react';
import {
    Button,
    DataTable,
    FilterButton,
    List,
    TextInput,
    useNotify,
    useRecordSelection,
    useTranslate,
    useUnselectAll,
    useUpdate,
} from 'react-admin';
import { Outlet, useNavigate, useParams } from 'react-router';

const renderAgencyGroups = (groups: GroupUser[] | undefined) => {
    if (!groups || groups.length === 0) return null;

    // Filtrar solo los grupos que tienen identifier de agencia
    const agencyGroups = groups.filter((group: GroupUser) => {
        const identifiers = (group as any)?.identifiers || [];
        return identifiers.some((identifier: string) => identifier?.startsWith('big:agency:'));
    });

    if (agencyGroups.length === 0) return null;

    return (
        <span>
            {agencyGroups.map((group: GroupUser, index: number) => (
                <span key={group.id}>
                    {group.name}
                    {index < agencyGroups.length - 1 && ', '}
                </span>
            ))}
        </span>
    );
};

const isUserBelongingToAgency = (user: User): boolean => {
    const groups = user.groups as any as GroupUser[] | undefined;
    if (!groups || groups.length === 0) {
        return false;
    }

    return groups.some((group: GroupUser) => {
        const identifiers = (group as any)?.identifiers || [];
        return identifiers.some((identifier: string) => identifier?.startsWith('big:agency:'));
    });
};

const UsersToAgencyListDialog = () => {
    const navigate = useNavigate();
    const translate = useTranslate();
    const { agencyId } = useParams();
    const [update, { isLoading }] = useUpdate();
    const notify = useNotify();
    const queryClient = useQueryClient();
    const [multiple, setMultiple] = useState(false);
    const [selectedUsers, { clearSelection }] = useRecordSelection({ resource: USERS, storeKey: 'compactUsersList' });
    const unselectAll = useUnselectAll(USERS);

    const navigateBack = () => {
        navigate(-1);
    };

    const handleAddMultipleToAgency = () => {
        if (selectedUsers.length === 0) {
            notify('No hay usuarios seleccionados', { type: 'warning' });
            return;
        }

        const promises = map(selectedUsers, (user: string) =>
            update(
                USERS,
                {
                    id: `${user}/add_group_users`,
                    data: {
                        groupUsers: [composeUri(GROUP_USERS, agencyId)],
                    },
                },
                {
                    returnPromise: true,
                },
            ),
        );

        Promise.all(promises).then(() => {
            queryClient.invalidateQueries({ queryKey: [USERS] });
            notify('agency.user_added', { type: 'success', messageArgs: { smart_count: selectedUsers.length } });
            clearSelection();
            setMultiple(false);
        });
    };

    return (
        <DialogTemplate
            data-cy={'compact-users-list-dialog'}
            maxWidth={'md'}
            icon={<PersonOutlined />}
            open={true}
            onClose={navigateBack}
            title={translate('main.navigation.link_users')}
        >
            <ListStylesContainer>
                <List
                    actions={
                        <TopToolbar sx={{ display: 'flex', alignItems: 'center' }}>
                            <FilterButton />

                            <Button
                                data-cy='add-multiple-to-agency-button'
                                startIcon={multiple ? <CancelOutlined /> : <AddIcon />}
                                label={multiple ? 'ra.action.cancel' : 'agency.list.client.add'}
                                onClick={() => {
                                    setMultiple(!multiple);
                                    if (!multiple) {
                                        unselectAll();
                                    } else {
                                        clearSelection();
                                    }
                                }}
                            />

                            <RefreshButton
                                data-cy='refresh-button'
                                style={{
                                    paddingLeft: '12px',
                                    paddingRight: '12px',
                                }}
                            />
                        </TopToolbar>
                    }
                    component='div'
                    data-cy={'agency_users_list'}
                    resource={USERS}
                    filter={{
                        'profiles.name': [BIG_AGENCY_MANAGER, BIG_AGENCY_OPERATOR],
                        hidden: false,
                        isSemiAnonymous: false,
                        serialization_groups: ['read_groups'],
                    }}
                    sort={{ order: 'DESC', field: 'createdAt' }}
                    empty={false}
                    storeKey='compactUsersList'
                    disableSyncWithLocation
                    filters={filters}
                    queryOptions={{
                        enabled: !!agencyId,
                    }}
                >
                    <DataTable
                        isRowSelectable={(user: User) => !isUserBelongingToAgency(user)}
                        bulkActionButtons={
                            multiple ? (
                                <LoadingButton loading={isLoading} onClick={handleAddMultipleToAgency}>
                                    {translate('ra.action.confirm')}
                                </LoadingButton>
                            ) : (
                                false
                            )
                        }
                    >
                        <DataTable.Col source='lastName' label={'main.navigation.last_name'} />

                        <DataTable.Col source='firstName' label={'main.navigation.name'} />

                        <DataTable.Col source='email' label={'main.navigation.email'} />

                        <DataTable.Col
                            label={'agency.form.agency_name'}
                            render={(user: User) => renderAgencyGroups(user.groups as any as GroupUser[])}
                        />
                    </DataTable>
                </List>
            </ListStylesContainer>

            <Outlet />
        </DialogTemplate>
    );
};

const filters = [
    <TextInput source='lastName' label={'main.navigation.last_name'} variant='standard' resettable />,
    <TextInput source='firstName' label={'main.navigation.name'} variant='standard' resettable />,
    <TextInput source='email' label='main.navigation.email' variant='standard' resettable />,
];

export default UsersToAgencyListDialog;
